import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { useLocation } from 'react-router'
import { Body } from '../Shell/Shell'
import './RecordDetailsPage.scss'
import { toggleSidebarCollapse, overrideSidebarResizing } from '../../actions'
import BusinessEntityRecordDetails from './BusinessEntity/BusinessEntityRecordDetails'
import TrademarkRecordDetails from './Trademark/TrademarkRecordDetails'
import UccRecordDetails from './UCC/UccRecordDetails'
import UCCInfoRequestRecordDetails from './UCCInfoRequest/UCCInfoRequestRecordDetails'
import RegisterdAgentRecordDetails from './RegisteredAgent/RegisteredAgentRecordDetails'
import DataRequestRecordDetails from './DataRequest/DataRequestRecordDetails'
import OrderRecordDetails from './Orders/OrderRecordDetails'
import NotaryRecordDetails from './Notary/NotaryRecordDetails'
import ApostilleRecordDetails from './Apostille/ApostilleRecordDetails'
import CharityRecordDetails from './Charity/CharityRecordDetails'
import { CLEAR_RECORD_DATA } from './RecordDetailsReducer'
import ContractorRecordDetails from './Contractor/ContractorRecordDetails'
import LobbyistRecordDetails from './Lobbyist/LobbyistRecordDetails'
import { SUBROUTES } from '../../App'
import MiscRecordDetails from './Misc/MiscRecordDetails'

const RecordDetailsPage = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)

  const recordType = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 2]
  }, [location.pathname])

  const isMiscRecordsType = useMemo(() => {
    const route = SUBROUTES.find((r) => r.parameter === recordType)
    return !!(route && route.IsMiscRecordsType)
  }, [recordType])

  useEffect(() => {
    dispatch(overrideSidebarResizing(true))
    dispatch(toggleSidebarCollapse(true))
    return () => {
      dispatch(overrideSidebarResizing(false))
      dispatch(toggleSidebarCollapse(false))
      dispatch({ type: CLEAR_RECORD_DATA })
    }
  }, [])

  const doesUserHaveViewAccess = useMemo(() => {
    if (isMiscRecordsType) return appUserRights.includes('MiscellaneousView')
    switch (recordType) {
      case 'ApostilleEntitySearch':
        return appUserRights.includes('ApostilleView')
      case 'BusinessEntitySearch':
        return appUserRights.includes('BusinessView')
      case 'CertificationSearch':
        return appUserRights.includes('OrdersView')
      case 'CharitableOrganizationSearch':
        return appUserRights.includes('CharityView')
      case 'ContractorSearch':
        return appUserRights.includes('ContractorView')
      case 'DataRequestSearch':
        return appUserRights.includes('DataRequestView')
      case 'LobbyistSearch':
        return appUserRights.includes('LobbyistView')
      case 'NotaryEntitySearch':
        return appUserRights.includes('NotaryView')
      case 'RegisteredAgentSearch':
        return appUserRights.includes('RegisteredAgentView')
      case 'TrademarkSearch':
        return appUserRights.includes('TrademarkView')
      case 'UCCInfoRequestEntitySearch':
      case 'UCCSearch':
        return appUserRights.includes('UCCView')
      default:
        return false
    }
  }, [appUserRights, recordType, isMiscRecordsType])

  if (!doesUserHaveViewAccess) {
    return (
      <Body className="record-details">
        <h1>Access to record is denied.</h1>
      </Body>
    )
  }

  return (
    <Body className="record-details">
      <div className="record-details-wrapper">
        {recordType === 'ApostilleEntitySearch' && <ApostilleRecordDetails />}
        {recordType === 'BusinessEntitySearch' && <BusinessEntityRecordDetails />}
        {recordType === 'CertificationSearch' && <OrderRecordDetails />}
        {recordType === 'CharitableOrganizationSearch' && <CharityRecordDetails />}
        {recordType === 'ContractorSearch' && <ContractorRecordDetails /> }
        {recordType === 'DataRequestSearch' && <DataRequestRecordDetails />}
        {recordType === 'LobbyistSearch' && <LobbyistRecordDetails />}
        {recordType === 'NotaryEntitySearch' && <NotaryRecordDetails />}
        {recordType === 'RegisteredAgentSearch' && <RegisterdAgentRecordDetails />}
        {recordType === 'TrademarkSearch' && <TrademarkRecordDetails />}
        {recordType === 'UCCInfoRequestEntitySearch' && <UCCInfoRequestRecordDetails />}
        {recordType === 'UCCSearch' && <UccRecordDetails />}
        {isMiscRecordsType && <MiscRecordDetails />}
      </div>
    </Body>
  )
}

export default RecordDetailsPage
